import { Component, OnInit, Input, Output, ViewChild, EventEmitter } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  NgForm,
  Validators
} from '@angular/forms';
import { omit, get } from 'lodash';
import { CommonRegEx } from '@nrwl/common-lib';
import { LdapConfigWizardService } from '../../../services/ldap-servers/ldap.server.service';
import { IComponentStateHistory, IValueChangedEvent } from '../../../wizard.content';
import { REMOVED } from '../../../services/base.wizard.step';

@Component({
  selector: 'nrwl-ldap.server.configuration',
  templateUrl: './ldap.server.configuration.component.html',
  styleUrls: ['./ldap.server.configuration.component.scss']
})
export class LdapServerConfigurationComponent implements OnInit {
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() step: any;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() subStep: any;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() wizardCtrl: any;
  @Input() componentId: string;
  @Input() stateHistory?: IComponentStateHistory;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() previousOutput: any;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>();
  @ViewChild('currentForm') form: NgForm;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  servers: any;
  bindDN: string;
  bindPassword: string;
  trustCertificate: string;
  baseDN: string;
  usersFilter: string;
  anonymousBind: boolean;
  trustSSLCertificate: boolean;
  groupBaseDN: string;
  groupsFilter: string;
  groupMembershipAttribute: string;
  usernameAttribute: string;
  nameAttribute: string;
  surnameAttribute: string;
  emailAttribute: string;
  membershipAttribute: string;
  groupNameAttribute: string;
  groupFilterBy: string;
  hide: boolean;
  ldapServerForm: UntypedFormGroup;
  get serversInput(): AbstractControl {
    return this.ldapServerForm.get('servers');
  }
  get baseDNInput(): AbstractControl {
    return this.ldapServerForm.get('baseDN');
  }
  get usersFilterInput(): AbstractControl {
    return this.ldapServerForm.get('usersFilter');
  }
  get usernameAttributeInput(): AbstractControl {
    return this.ldapServerForm.get('usernameAttribute');
  }
  get surnameAttributeInput(): AbstractControl {
    return this.ldapServerForm.get('surnameAttribute');
  }
  get emailAttributeInput(): AbstractControl {
    return this.ldapServerForm.get('emailAttribute');
  }
  get membershipAttributeInput(): AbstractControl {
    return this.ldapServerForm.get('membershipAttribute');
  }
  get groupNameAttributeInput(): AbstractControl {
    return this.ldapServerForm.get('groupNameAttribute');
  }
  get nameAttributeInput(): AbstractControl {
    return this.ldapServerForm.get('nameAttribute');
  }

  constructor(private wizardService: LdapConfigWizardService, private fb: UntypedFormBuilder) {
    this.hide = true;
  }

  ngOnInit(): void {
    this.createForm();
    this.step.form = this.ldapServerForm;
    if (this.wizardService.editMode) {
      this.resetData();
    } else {
      this.setDefaultData();
    }
    this.resetOutputFromParent();
  }

  serversUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'serversInput',
      key: 'servers',
      newValue: this.servers,
      isValid: this.ldapServerForm.controls.servers.valid ?? false
    });
  }

  bindDNUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'bindDNInput',
      key: 'bindDN',
      newValue: this.bindDN,
      isValid: true
    });
  }
  baseDNUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'baseDNInput',
      key: 'baseDN',
      newValue: this.baseDN,
      isValid: this.ldapServerForm.controls.baseDN.valid
    });
  }
  usersFilterUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'usersFilterInput',
      key: 'usersFilter',
      newValue: this.usersFilter,
      isValid: this.ldapServerForm.controls.usersFilter.valid
    });
  }
  usernameAttributeUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'usernameAttributeInput',
      key: 'usernameAttribute',
      newValue: this.usernameAttribute,
      isValid: this.ldapServerForm.controls.usernameAttribute.valid
    });
  }

  anonymousBindUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'anonymousBindInput',
      key: 'anonymousBind',
      newValue: this.anonymousBind ?? false,
      isValid: true
    });
  }

  trustSSLCertificateUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'trustSSLCertificateInput',
      key: 'trustSSLCertificate',
      newValue: this.trustSSLCertificate ?? false,
      isValid: true
    });
  }

  bindPasswordUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'bindPasswordInput',
      key: 'bindPassword',
      newValue: this.bindPassword,
      isValid: true
    });
  }

  trustCertificateUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'trustCertificateInput',
      key: 'trustCertificate',
      newValue: this.trustCertificate,
      isValid: true
    });
  }

  groupBaseDNUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'groupBaseDNInput',
      key: 'groupBaseDN',
      newValue: this.groupBaseDN,
      isValid: true
    });
  }

  groupsFilterUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'groupsFilterInput',
      key: 'groupsFilter',
      newValue: this.groupsFilter,
      isValid: true
    });
  }

  groupMembershipAttributeUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'groupMembershipAttributeInput',
      key: 'groupMembershipAttribute',
      newValue: this.groupMembershipAttribute,
      isValid: true
    });
  }

  nameAttributeUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'nameAttributeInput',
      key: 'nameAttribute',
      newValue: this.nameAttribute,
      isValid: this.ldapServerForm.controls.nameAttribute.valid
    });
  }

  surnameAttributeUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'surnameAttributeInput',
      key: 'surnameAttribute',
      newValue: this.surnameAttribute,
      isValid: this.ldapServerForm.controls.surnameAttribute.valid
    });
  }

  emailAttributeUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'emailAttributeInput',
      key: 'emailAttribute',
      newValue: this.emailAttribute,
      isValid: this.ldapServerForm.controls.emailAttribute.valid
    });
  }

  membershipAttributeUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'membershipAttributeInput',
      key: 'membershipAttribute',
      newValue: this.membershipAttribute,
      isValid: this.ldapServerForm.controls.membershipAttribute.valid
    });
  }

  groupNameAttributeUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'groupNameAttributeInput',
      key: 'groupNameAttribute',
      newValue: this.groupNameAttribute,
      isValid: this.ldapServerForm.controls.groupNameAttribute.valid
    });
  }

  groupFilterByUpdated(): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      inputId: 'groupFilterByInput',
      key: 'groupFilterBy',
      newValue: this.groupFilterBy,
      isValid: true
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validateServer(control: AbstractControl): any {
    if (control.value && control.value.length > 0) {
      const serverArray = control.value.split(',');
      let err = false;
      serverArray.forEach(element => {
        if (!element.trim().match(CommonRegEx.IP_PORT)) {
          err = true;
        }
      });
      if (err) {
        return { matchPattern: true };
      }
    }
  }

  createForm(): void {
    this.ldapServerForm = this.fb.group({});
    this.ldapServerForm.addControl(
      'servers',
      new UntypedFormControl('', [Validators.required, this.validateServer.bind(this)])
    );
    this.ldapServerForm.addControl('baseDN', new UntypedFormControl('', [Validators.required]));
    this.ldapServerForm.addControl('usersFilter', new UntypedFormControl('', [Validators.required]));
    this.ldapServerForm.addControl('usernameAttribute', new UntypedFormControl('', [Validators.required]));
    this.ldapServerForm.addControl('nameAttribute', new UntypedFormControl('', [Validators.required]));
    this.ldapServerForm.addControl('surnameAttribute', new UntypedFormControl('', [Validators.required]));
    this.ldapServerForm.addControl('emailAttribute', new UntypedFormControl('', [Validators.required]));
    this.ldapServerForm.addControl(
      'membershipAttribute',
      new UntypedFormControl('', [Validators.required])
    );
    this.ldapServerForm.addControl(
      'groupNameAttribute',
      new UntypedFormControl('', [Validators.required])
    );
    this.ldapServerForm.addControl('bindDN', new UntypedFormControl());
    this.ldapServerForm.addControl('bindPassword', new UntypedFormControl());
    this.ldapServerForm.addControl('anonymousBind', new UntypedFormControl());
    this.ldapServerForm.addControl('trustCertificate', new UntypedFormControl());
    this.ldapServerForm.addControl('trustSSLCertificate', new UntypedFormControl());
    this.ldapServerForm.addControl('groupBaseDN', new UntypedFormControl());
    this.ldapServerForm.addControl('groupsFilter', new UntypedFormControl());
    this.ldapServerForm.addControl('groupMembershipAttribute', new UntypedFormControl());
    this.ldapServerForm.addControl('groupFilterBy', new UntypedFormControl());
  }

  resetData(): void {
    this.wizardService.currentStep.output = omit(this.wizardService.currentStep.output, 'id');
    this.servers = get(this.previousOutput, 'servers');
    this.bindDN = get(this.previousOutput, 'bindDN');
    this.bindPassword = get(this.previousOutput, 'bindPassword');
    this.anonymousBind = get(this.previousOutput, 'anonymousBind');
    this.trustCertificate = get(this.previousOutput, 'trustCertificate');
    this.trustSSLCertificate = get(this.previousOutput, 'trustSSLCertificate');
    this.baseDN = get(this.previousOutput, 'baseDN');
    this.usersFilter = get(this.previousOutput, 'usersFilter');
    this.groupBaseDN = get(this.previousOutput, 'groupBaseDN');
    this.groupsFilter = get(this.previousOutput, 'groupsFilter');
    this.groupMembershipAttribute = get(this.previousOutput, 'groupMembershipAttribute');
    this.usernameAttribute = get(this.previousOutput, 'usernameAttribute');
    this.nameAttribute = get(this.previousOutput, 'nameAttribute');
    this.surnameAttribute = get(this.previousOutput, 'surnameAttribute');
    this.emailAttribute = get(this.previousOutput, 'emailAttribute');
    this.membershipAttribute = get(this.previousOutput, 'membershipAttribute');
    this.groupNameAttribute = get(this.previousOutput, 'groupNameAttribute');
    this.groupFilterBy = get(this.previousOutput, 'groupFilterBy');
    this.setLdapFormData();
  }
  setDefaultData(): void {
    this.usersFilter = '(objectClass=posixAccount)';
    this.groupsFilter = '(objectClass=groupOfUniqueNames)';
    this.groupMembershipAttribute = 'uniqueMember';
    this.usernameAttribute = 'cn';
    this.nameAttribute = 'givenName';
    this.surnameAttribute = 'sn';
    this.emailAttribute = 'mail';
    this.membershipAttribute = 'memberOf';
    this.groupNameAttribute = 'cn';
    this.anonymousBind = false;
    this.trustSSLCertificate = false;
    this.groupFilterBy = 'dn';
    this.ldapServerForm.get('anonymousBind').setValue(false);
    this.ldapServerForm.get('usersFilter').setValue('(objectClass=posixAccount)');
    this.ldapServerForm.get('groupsFilter').setValue('(objectClass = groupOfUniqueNames)');
    this.ldapServerForm.get('groupMembershipAttribute').setValue('uniqueMember');
    this.ldapServerForm.get('usernameAttribute').setValue('cn');
    this.ldapServerForm.get('nameAttribute').setValue('givenName');
    this.ldapServerForm.get('surnameAttribute').setValue('sn');
    this.ldapServerForm.get('emailAttribute').setValue('mail');
    this.ldapServerForm.get('membershipAttribute').setValue('memberOf');
    this.ldapServerForm.get('groupNameAttribute').setValue('cn');
    this.ldapServerForm.get('trustSSLCertificate').setValue(false);
    this.ldapServerForm.get('groupFilterBy').setValue('dn');
  }

  setLdapFormData(): void {
    this.ldapServerForm.get('servers').setValue(this.servers, 'servers');
    this.ldapServerForm.get('bindDN').setValue(this.bindDN, 'bindDN');
    this.ldapServerForm.get('bindPassword').setValue(this.bindPassword, 'bindPassword');
    this.ldapServerForm.get('anonymousBind').setValue(this.anonymousBind, 'anonymousBind');
    this.ldapServerForm.get('trustCertificate').setValue(this.trustCertificate, 'trustCertificate');
    this.ldapServerForm
      .get('trustSSLCertificate')
      .setValue(this.trustSSLCertificate, 'trustSSLCertificate');
    this.ldapServerForm.get('baseDN').setValue(this.baseDN, 'baseDN');
    this.ldapServerForm.get('usersFilter').setValue(this.usersFilter, 'usersFilter');
    this.ldapServerForm.get('groupBaseDN').setValue(this.groupBaseDN, 'groupBaseDN');
    this.ldapServerForm.get('groupsFilter').setValue(this.groupsFilter, 'groupsFilter');
    this.ldapServerForm
      .get('groupMembershipAttribute')
      .setValue(this.groupMembershipAttribute, 'groupMembershipAttribute');
    this.ldapServerForm
      .get('usernameAttribute')
      .setValue(this.usernameAttribute, 'usernameAttribute');
    this.ldapServerForm.get('nameAttribute').setValue(this.nameAttribute, 'nameAttribute');
    this.ldapServerForm.get('surnameAttribute').setValue(this.surnameAttribute, 'surnameAttribute');
    this.ldapServerForm.get('emailAttribute').setValue(this.emailAttribute, 'emailAttribute');
    this.ldapServerForm
      .get('membershipAttribute')
      .setValue(this.membershipAttribute, 'membershipAttribute');
    this.ldapServerForm
      .get('groupNameAttribute')
      .setValue(this.groupNameAttribute, 'groupNameAttribute');
    this.ldapServerForm.get('groupFilterBy').setValue(this.groupFilterBy, 'groupFilterBy');
  }

  resetOutputFromParent(): void {
    const server = this.stateHistory(this.componentId, 'serversInput');
    if (server && server.value && server.value !== REMOVED) {
      this.servers = server.value;
    }
    const baseDN = this.stateHistory(this.componentId, 'baseDNInput');
    if (baseDN && baseDN.value && baseDN.value !== REMOVED) {
      this.baseDN = baseDN.value;
    }
    const bindDn = this.stateHistory(this.componentId, 'bindDNInput');
    if (bindDn && bindDn.value && bindDn.value !== REMOVED) {
      this.bindDN = bindDn.value;
    }

    const trustCertificate = this.stateHistory(this.componentId, 'trustCertificateInput');
    if (trustCertificate && trustCertificate.value && trustCertificate.value !== REMOVED) {
      this.trustCertificate = trustCertificate.value;
    }
    const usersFilter = this.stateHistory(this.componentId, 'usersFilterInput');
    if (usersFilter && usersFilter.value && usersFilter.value !== REMOVED) {
      this.usersFilter = usersFilter.value;
    }
    const groupBaseDN = this.stateHistory(this.componentId, 'groupBaseDNInput');
    if (groupBaseDN && groupBaseDN.value && groupBaseDN.value !== REMOVED) {
      this.groupBaseDN = groupBaseDN.value;
    }
    const groupsFilter = this.stateHistory(this.componentId, 'groupsFilterInput');
    if (groupsFilter && groupsFilter.value && groupsFilter.value !== REMOVED) {
      this.groupsFilter = groupsFilter.value;
    }
    const groupMembershipAttribute = this.stateHistory(
      this.componentId,
      'groupMembershipAttributeInput'
    );
    if (
      groupMembershipAttribute &&
      groupMembershipAttribute.value &&
      groupMembershipAttribute.value !== REMOVED
    ) {
      this.groupMembershipAttribute = groupMembershipAttribute.value;
    }

    const usernameAttribute = this.stateHistory(this.componentId, 'usernameAttributeInput');
    if (usernameAttribute && usernameAttribute.value && usernameAttribute.value !== REMOVED) {
      this.usernameAttribute = usernameAttribute.value;
    }

    const nameAttribute = this.stateHistory(this.componentId, 'nameAttributeInput');
    if (nameAttribute && nameAttribute.value && nameAttribute.value !== REMOVED) {
      this.nameAttribute = nameAttribute.value;
    }

    const surnameAttribute = this.stateHistory(this.componentId, 'surnameAttributeInput');
    if (surnameAttribute && surnameAttribute.value && surnameAttribute.value !== REMOVED) {
      this.surnameAttribute = surnameAttribute.value;
    }

    const emailAttribute = this.stateHistory(this.componentId, 'emailAttributeInput');
    if (emailAttribute && emailAttribute.value && emailAttribute.value !== REMOVED) {
      this.emailAttribute = emailAttribute.value;
    }
    const membershipAttribute = this.stateHistory(this.componentId, 'membershipAttributeInput');
    if (membershipAttribute && membershipAttribute.value && membershipAttribute.value !== REMOVED) {
      this.membershipAttribute = membershipAttribute.value;
    }
    const groupNameAttribute = this.stateHistory(this.componentId, 'groupNameAttributeInput');
    if (groupNameAttribute && groupNameAttribute.value && groupNameAttribute.value !== REMOVED) {
      this.groupNameAttribute = groupNameAttribute.value;
    }
    const trustSSLCertificate = this.stateHistory(this.componentId, 'trustSSLCertificateInput');
    if (trustSSLCertificate && trustSSLCertificate.value && trustSSLCertificate.value !== REMOVED) {
      this.trustSSLCertificate = trustSSLCertificate.value;
    }
    const bindPassword = this.stateHistory(this.componentId, 'bindPasswordInput');
    if (bindPassword && bindPassword.value && bindPassword.value !== REMOVED) {
      this.bindPassword = bindPassword.value;
    }

    const anonymousBind = this.stateHistory(this.componentId, 'anonymousBindInput');
    if (anonymousBind && anonymousBind.value && anonymousBind.value !== REMOVED) {
      this.anonymousBind = anonymousBind.value;
    }
    this.setLdapFormData();
  }
}
